import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, CircleAlert, MessageSquareText, Send, Ship, Users, WifiOff } from "lucide-react";
import { useState, type FormEvent } from "react";
import type { InternalMessageAudience, InternalMessagePriority } from "@mola/contracts";
import { api } from "../api";
import { useI18n } from "../i18n";

interface InternalMessage {
  id: string;
  audience: InternalMessageAudience;
  priority: InternalMessagePriority;
  body: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
}

const copy = {
  en: {
    title: "Internal notes", intro: "Short operational updates for this departure. Everyone with access to the departure can read this feed.",
    delivery: "Stored in Dive Ready only. This does not send a push notification, SMS, email or WhatsApp message.", add: "Add a note", audience: "For",
    base: "Base", crew: "Boat / crew", all: "Everyone", priority: "Priority", normal: "Normal", important: "Important", message: "Message",
    placeholder: "e.g. Special guest is on board — please brief the crew.", privacy: "Keep it operational. Do not include medical details, passwords or payment data.",
    post: "Post note", posting: "Posting…", posted: "Internal note posted.", error: "The note could not be posted. Check the connection and try again.",
    loadError: "Internal notes could not be loaded.", empty: "No internal notes yet", emptyBody: "Post the first short update for the base or crew.",
    readOnly: "You can read internal notes, but your role cannot post them.", offline: "Connect to post this note. Your draft stays here.", archived: "This departure is archived and cannot receive new notes.",
    updating: "The feed updates automatically while this departure is open.", characters: "characters remaining",
  },
  de: {
    title: "Interne Notizen", intro: "Kurze betriebliche Meldungen zu dieser Ausfahrt. Alle Mitarbeiter mit Zugriff auf die Ausfahrt können diesen Verlauf lesen.",
    delivery: "Nur in Dive Ready gespeichert. Es wird keine Push-, SMS-, E-Mail- oder WhatsApp-Nachricht versendet.", add: "Notiz hinzufügen", audience: "Für",
    base: "Basis", crew: "Boot / Crew", all: "Alle", priority: "Wichtigkeit", normal: "Normal", important: "Wichtig", message: "Nachricht",
    placeholder: "z. B. Special Guest ist an Bord — bitte die Crew informieren.", privacy: "Nur betrieblich nutzen. Keine medizinischen Angaben, Passwörter oder Zahlungsdaten eintragen.",
    post: "Notiz veröffentlichen", posting: "Wird veröffentlicht…", posted: "Interne Notiz veröffentlicht.", error: "Die Notiz konnte nicht veröffentlicht werden. Verbindung prüfen und erneut versuchen.",
    loadError: "Interne Notizen konnten nicht geladen werden.", empty: "Noch keine internen Notizen", emptyBody: "Veröffentlichen Sie die erste kurze Meldung für Basis oder Crew.",
    readOnly: "Sie können interne Notizen lesen, mit Ihrer Rolle aber keine veröffentlichen.", offline: "Zum Veröffentlichen ist eine Verbindung erforderlich. Ihr Entwurf bleibt erhalten.", archived: "Diese Ausfahrt ist archiviert und kann keine neuen Notizen erhalten.",
    updating: "Der Verlauf wird automatisch aktualisiert, solange diese Ausfahrt geöffnet ist.", characters: "Zeichen übrig",
  },
  es: {
    title: "Notas internas", intro: "Actualizaciones operativas breves de esta salida. Todo el personal con acceso a la salida puede leer este historial.",
    delivery: "Se guarda solo en Dive Ready. No envía notificaciones push, SMS, correo ni WhatsApp.", add: "Añadir una nota", audience: "Para",
    base: "Base", crew: "Barco / tripulación", all: "Todos", priority: "Prioridad", normal: "Normal", important: "Importante", message: "Mensaje",
    placeholder: "p. ej., Hay un invitado especial a bordo; informa a la tripulación.", privacy: "Uso exclusivamente operativo. No incluyas datos médicos, contraseñas ni datos de pago.",
    post: "Publicar nota", posting: "Publicando…", posted: "Nota interna publicada.", error: "No se pudo publicar la nota. Comprueba la conexión e inténtalo de nuevo.",
    loadError: "No se pudieron cargar las notas internas.", empty: "Aún no hay notas internas", emptyBody: "Publica la primera actualización breve para la base o la tripulación.",
    readOnly: "Puedes leer las notas internas, pero tu rol no permite publicarlas.", offline: "Conéctate para publicar la nota. El borrador permanece aquí.", archived: "Esta salida está archivada y no admite notas nuevas.",
    updating: "El historial se actualiza automáticamente mientras la salida está abierta.", characters: "caracteres disponibles",
  },
  fr: {
    title: "Notes internes", intro: "Courtes informations opérationnelles pour cette sortie. Tout le personnel ayant accès à la sortie peut lire ce fil.",
    delivery: "Enregistré uniquement dans Dive Ready. Aucun push, SMS, e-mail ou message WhatsApp n'est envoyé.", add: "Ajouter une note", audience: "Pour",
    base: "Base", crew: "Bateau / équipage", all: "Tout le monde", priority: "Priorité", normal: "Normale", important: "Importante", message: "Message",
    placeholder: "ex. Un invité spécial est à bord — merci de prévenir l'équipage.", privacy: "Usage opérationnel uniquement. N'ajoutez aucune donnée médicale, aucun mot de passe ou paiement.",
    post: "Publier la note", posting: "Publication…", posted: "Note interne publiée.", error: "La note n'a pas pu être publiée. Vérifiez la connexion et réessayez.",
    loadError: "Les notes internes n'ont pas pu être chargées.", empty: "Aucune note interne", emptyBody: "Publiez la première information courte pour la base ou l'équipage.",
    readOnly: "Vous pouvez lire les notes internes, mais votre rôle ne permet pas d'en publier.", offline: "Connectez-vous pour publier cette note. Le brouillon reste ici.", archived: "Cette sortie est archivée et ne peut plus recevoir de note.",
    updating: "Le fil se met à jour automatiquement tant que cette sortie est ouverte.", characters: "caractères disponibles",
  },
} as const;

const audienceIcons = { BASE: Building2, CREW: Ship, ALL: Users } as const;

export function InternalMessagesPanel({ departureId, canWrite, online, archived }: { departureId: string; canWrite: boolean; online: boolean; archived: boolean }) {
  const { language } = useI18n();
  const c = copy[language];
  const queryClient = useQueryClient();
  const [audience, setAudience] = useState<InternalMessageAudience>("ALL");
  const [priority, setPriority] = useState<InternalMessagePriority>("NORMAL");
  const [body, setBody] = useState("");
  const [success, setSuccess] = useState("");
  const messages = useQuery({
    queryKey: ["internal-messages", departureId],
    queryFn: () => api<InternalMessage[]>(`/api/v1/dive-ready/departures/${departureId}/internal-messages`),
    enabled: online,
    refetchInterval: online ? 15_000 : false,
  });
  const create = useMutation({
    mutationFn: () => api<InternalMessage>(`/api/v1/dive-ready/departures/${departureId}/internal-messages`, {
      method: "POST",
      body: JSON.stringify({ audience, priority, body }),
    }),
    onSuccess: async () => {
      setBody("");
      setPriority("NORMAL");
      setSuccess(c.posted);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["internal-messages", departureId] }),
        queryClient.invalidateQueries({ queryKey: ["activity", departureId] }),
      ]);
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSuccess("");
    if (body.trim() && online && canWrite && !archived) create.mutate();
  };
  const audienceLabel = (value: InternalMessageAudience) => value === "BASE" ? c.base : value === "CREW" ? c.crew : c.all;

  return (
    <div className="internal-messages" data-help-id="internal-messages">
      <div className="internal-messages-heading">
        <div><h2><MessageSquareText />{c.title}</h2><p>{c.intro}</p></div>
        <p className="internal-messages-refresh">{c.updating}</p>
      </div>
      <div className="internal-delivery-note"><CircleAlert /><span>{c.delivery}</span></div>

      {canWrite && !archived && (
        <form className="internal-message-composer" onSubmit={submit}>
          <h3>{c.add}</h3>
          <div className="internal-message-controls">
            <label><span>{c.audience}</span><select value={audience} onChange={(event) => setAudience(event.target.value as InternalMessageAudience)}><option value="BASE">{c.base}</option><option value="CREW">{c.crew}</option><option value="ALL">{c.all}</option></select></label>
            <label><span>{c.priority}</span><select value={priority} onChange={(event) => setPriority(event.target.value as InternalMessagePriority)}><option value="NORMAL">{c.normal}</option><option value="IMPORTANT">{c.important}</option></select></label>
          </div>
          <label className="internal-message-body"><span>{c.message}</span><textarea rows={3} maxLength={500} value={body} onChange={(event) => { setBody(event.target.value); setSuccess(""); }} placeholder={c.placeholder} /></label>
          <div className="internal-message-guidance"><small>{c.privacy}</small><small>{500 - body.length} {c.characters}</small></div>
          {!online && <div className="inline-offline"><WifiOff />{c.offline}</div>}
          {create.isError && <div className="form-error" role="alert">{c.error}</div>}
          {success && <div className="success-message" role="status">{success}</div>}
          <button className="button button-primary internal-message-submit" disabled={!body.trim() || !online || create.isPending} type="submit"><Send />{create.isPending ? c.posting : c.post}</button>
        </form>
      )}

      {!canWrite && <div className="internal-message-readonly">{c.readOnly}</div>}
      {archived && <div className="internal-message-readonly">{c.archived}</div>}
      {messages.isError && online && <div className="error-block">{c.loadError}</div>}
      {messages.isLoading && online ? <div className="loading-block" /> : messages.data?.length ? (
        <ol className="internal-message-feed">
          {messages.data.map((message) => {
            const AudienceIcon = audienceIcons[message.audience];
            return <li className={message.priority === "IMPORTANT" ? "important" : ""} key={message.id}>
              <div className="internal-message-meta"><span className="internal-message-audience"><AudienceIcon />{audienceLabel(message.audience)}</span>{message.priority === "IMPORTANT" && <strong>{c.important}</strong>}<time dateTime={message.createdAt}>{new Intl.DateTimeFormat(language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(message.createdAt))}</time></div>
              <p>{message.body}</p>
              <span className="internal-message-author">{message.createdByName}</span>
            </li>;
          })}
        </ol>
      ) : !messages.isLoading && <div className="empty-state compact internal-message-empty"><MessageSquareText /><h3>{c.empty}</h3><p>{c.emptyBody}</p></div>}
    </div>
  );
}
